import {useTheme, useTransition} from '@yandex-delivery/blocks'
import {useMediaQuery} from '@yandex-delivery/blocks/legacy'
import type {FetchResult, ServerError} from '@yandex-delivery/core/utils/network'
import {useRefresh} from '@yandex-delivery/hooks'
import {get} from '@yandex-delivery/utils'
import {flatten, pickBy} from 'lodash'
import {useCallback, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {type OrderPoint, fetchEventsAuth, fetchPerformerPosition, fetchSharedRouteInfo} from 'src/api'
import type * as t from 'src/api/types'
import {isAuthMode} from 'src/shared/config'
import {isMobileDevice} from 'src/shared/utils'

import type {RouterParams} from './types'
import {isFeedbackAction, isPerformerPositionAvailable} from './utils'

const SHARED_ROUTE_INFO_KEY = 'route/shared-route-info'

const getDelayFromXRefreshAfter = (response?: FetchResult<t.SharedRouteInfoResponse>) => {
  const delay = get('headers.x-refresh-after', response) || 3000
  return Number(delay)
}

const isDelivered = (response?: FetchResult<t.SharedRouteInfoResponse>): boolean => {
  const routePoints = (get('body.route_points', response) || []) as OrderPoint[]
  if (routePoints.length === 0) {
    return false
  }
  return routePoints.every((point: OrderPoint) => point.visit_status === 'visited')
}

export const useSharedRoute = () => {
  return useTransition<FetchResult<t.SharedRouteInfoResponse>, ServerError>(undefined, {key: SHARED_ROUTE_INFO_KEY})
}

export const useSharedRoteInfoPolling = (sharingKey: string) => {
  const [{payload, error, isPending}, startTransition] = useTransition(() => fetchSharedRouteInfo({key: sharingKey}), {
    key: SHARED_ROUTE_INFO_KEY,
  })
  const getSharedRouteInfo = useCallback(
    () => startTransition(() => fetchSharedRouteInfo({key: sharingKey})),
    [sharingKey, startTransition],
  )
  const stopPoll = isPending || Boolean(error) || isDelivered(payload)

  useRefresh(stopPoll ? undefined : getSharedRouteInfo, {interval: getDelayFromXRefreshAfter(payload)})
}

export const useSharedRouteInfo = () => {
  const [{payload}] = useSharedRoute()

  return payload?.body
}

const PERFORMER_POSITION_KEY = 'route/performer-position'

export const useSharedRoutePerformer = () => {
  const location = useLocation<RouterParams>()
  const sharingKey = location.hash.substring(1)
  const info = useSharedRouteInfo()
  const isAvailable = isPerformerPositionAvailable(info)

  const [{payload, error}, startTransition] = useTransition<t.PerformerPositionResponseWithDirection, ServerError>(
    undefined,
    {
      key: PERFORMER_POSITION_KEY,
      shouldShowBaseError: false,
    },
  )

  const getPerformerPosition = useCallback(() => {
    startTransition(() => fetchPerformerPosition({key: sharingKey}))
  }, [sharingKey, startTransition])

  useRefresh(isAvailable && !error ? getPerformerPosition : undefined, {interval: 3000})

  return payload
}

/**
 * Events polling для авторизованной шарилки (auth).
 * Поллит /web/deliveries/events, фильтрует по delivery_id, при новых событиях триггерит обновление /web/state.
 */
export const useEventsPollingAuth = (sharingKey: string, triggerRefresh: () => void) => {
  const revisionRef = useRef<string | undefined>(undefined)
  const isBaselineRef = useRef<boolean>(true)
  const isFetchingRef = useRef<boolean>(false)
  const [eventsInterval, setEventsInterval] = useState(3000)
  const [{payload, error}] = useSharedRoute()

  const pollEvents = useCallback(() => {
    if (isFetchingRef.current) return

    isFetchingRef.current = true
    fetchEventsAuth({key: sharingKey, revision: revisionRef.current})
      .then(response => {
        // Всегда сохраняем revision (как в iOS lastRevision)
        revisionRef.current = response.revision

        // Обновляем interval из X-Refresh-After
        if (response.refreshAfterMs) {
          setEventsInterval(response.refreshAfterMs)
        }

        // Первый poll = baseline (backlog всех доставок)
        // Не триггерим refresh на baseline, только на incremental
        if (isBaselineRef.current) {
          isBaselineRef.current = false
        } else if (response.events.length > 0) {
          // Только при НОВЫХ events → trigger /web/state
          triggerRefresh()
        }
      })
      .catch(() => {
        // Ignore errors — polling продолжится на следующем интервале
      })
      .finally(() => {
        isFetchingRef.current = false
      })
  }, [sharingKey, triggerRefresh])

  const stopPoll = !isAuthMode || Boolean(error) || isDelivered(payload)

  useRefresh(stopPoll ? undefined : pollEvents, {interval: eventsInterval})
}

export const useIsMobile = (noSsr = true) => {
  const theme = useTheme()
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('sm'), {noSsr})
  const serverIsMobile = isMobileDevice()

  // Auth mode: доверяем server-side значению из uatraits
  if (isAuthMode && serverIsMobile !== undefined) {
    return serverIsMobile
  }

  return isMobileQuery
}

export const useFeedbackReasons = (score: number): t.FeedbackReason[] => {
  const info = useSharedRouteInfo()
  const actionFeedback = info?.actions.find(isFeedbackAction)

  if (actionFeedback === undefined || actionFeedback.reasons === undefined) {
    return []
  }

  const allReasons = actionFeedback.reasons.reduce<Record<string, t.FeedbackReason[]>>((acc, reason) => {
    const key = reason.scores.toString()
    return {...acc, [key]: (acc[key] || []).concat(reason)}
  }, {})

  return flatten(Object.values(pickBy(allReasons, (_, key) => key.includes(score.toString()))))
}

export const useFeedbackSubtitle = (currentScore?: number): string | undefined => {
  const info = useSharedRouteInfo()
  const actionFeedback = info?.actions.find(isFeedbackAction)
  const score = currentScore || actionFeedback?.last_feedback?.score

  if (!actionFeedback || !score) {
    return
  }

  const allSubtitles = actionFeedback.subtitles.reduce<Record<string, t.FeedbackSubtitle[]>>((acc, subtitle) => {
    const key = subtitle.scores.toString()
    return {...acc, [key]: (acc[key] || []).concat(subtitle)}
  }, {})

  return flatten(Object.values(pickBy(allSubtitles, (_, key) => key.includes(score.toString()))))[0]?.title
}
